<template>
    <card>
        <template slot="header">
            <h4 class="card-title">
                {{ $t("dashboard.idlerepairs_title") }}
                <p class="card-category text-muted">{{ $t("dashboard.idlerepairs_subtitle") }}</p>
            </h4>

            <div class="row align-items-end">
                <div class="col-lg-4 col-md-6 mb-2">
                    <label for="idle_status">{{ $t("field.status") }}</label>
                    <select id="idle_status" class="form-control" :value="filters.status_id" @change="setFilter('status_id', $event.target.value)">
                        <option value="">{{ $t("dashboard.idlerepairs_all_status") }}</option>
                        <option v-for="status in idleStatusList" :key="status.id" :value="status.id">{{ status.name }}</option>
                    </select>
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <label for="idle_period">{{ $t("dashboard.idlerepairs_period") }}</label>
                    <select id="idle_period" class="form-control" :value="filters.period" @change="setFilter('period', $event.target.value)">
                        <option value="">{{ $t("dashboard.idlerepairs_all_periods") }}</option>
                        <option v-for="period in periodOptions" :key="period" :value="period">{{ periodLabel(period) }}</option>
                    </select>
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <label for="idle_days">{{ $t("dashboard.idlerepairs_days") }}</label>
                    <input
                        id="idle_days"
                        type="number"
                        class="form-control"
                        :min="daysRange.min"
                        :max="daysRange.max"
                        :value="filters.days"
                        :placeholder="$t('dashboard.idlerepairs_all_days')"
                        @change="onDaysChange"
                    />
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <label>{{ $t("field.asf") }}</label>
                    <multiselect
                        v-model="asf_value"
                        :options="asfs"
                        :custom-label="asfLabel"
                        :placeholder="$t('text.select')"
                        label="name"
                        track-by="id"
                        @open="getAsfs"
                    ></multiselect>
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <label for="idle_date_from">{{ $t("dashboard.idlerepairs_date_from") }}</label>
                    <b-form-datepicker
                        id="idle_date_from"
                        :value="filters.date_from"
                        @input="setFilter('date_from', $event)"
                        size="sm"
                        :max="filters.date_to || null"
                        :date-format-options="dateFormat"
                        :placeholder="$t('dashboard.select_date')"
                        reset-button
                    ></b-form-datepicker>
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <label for="idle_date_to">{{ $t("dashboard.idlerepairs_date_to") }}</label>
                    <b-form-datepicker
                        id="idle_date_to"
                        :value="filters.date_to"
                        @input="setFilter('date_to', $event)"
                        size="sm"
                        :min="filters.date_from || null"
                        :date-format-options="dateFormat"
                        :placeholder="$t('dashboard.select_date')"
                        reset-button
                    ></b-form-datepicker>
                </div>
                <div class="col-lg-4 col-md-6 mb-2">
                    <a @click.prevent="clearFilters" class="btn btn-outline-primary px-2 rounded idle-action" href>
                        {{ $t("dashboard.idlerepairs_clear") }}
                    </a>
                    <button
                        type="button"
                        class="btn btn-success px-2 rounded ml-2 idle-action"
                        :disabled="exporting"
                        @click="exportExcel"
                    >
                        <i :class="['fas', 'mr-1', exporting ? 'fa-spinner fa-spin' : 'fa-file-excel']"></i>
                        {{ $t("dashboard.idlerepairs_export") }}
                    </button>
                </div>
            </div>
            <div v-if="activeFilters.length" class="idle-active-filters mt-2" role="group" :aria-label="$t('dashboard.idlerepairs_active_filters')">
                <span class="text-muted small">{{ $t("dashboard.idlerepairs_active_filters") }}:</span>
                <button
                    v-for="filter in activeFilters"
                    :key="filter.key"
                    type="button"
                    class="btn btn-sm btn-outline-primary rounded idle-action idle-filter-chip"
                    :aria-label="$t('dashboard.idlerepairs_remove_filter', { filter: filter.label })"
                    @click="removeFilter(filter.key)"
                >
                    {{ filter.label }} <i class="fas fa-times ml-1" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <v-server-table
            ref="vuetable"
            url="/dtable/repairs_idle"
            :columns="columns"
            :options="options"
            @loaded="onLoaded"
        >
            <template slot="repair_id" slot-scope="props">
                <div v-if="props.row.is_group" class="idle-group-title">
                    <span>{{ groupLabel(props.row.days) }}</span>
                </div>
                <template v-else>{{ props.row.repair_id }}</template>
            </template>
            <template slot="opened_at" slot-scope="props">
                <template v-if="!props.row.is_group">{{ props.row.opened_at | moment("DD/MM/YYYY") }}</template>
            </template>
            <template slot="days_since_opening" slot-scope="props">{{ props.row.days_since_opening }}</template>
            <template slot="id" slot-scope="props">
                <router-link
                    v-if="!props.row.is_group"
                    class="btn btn-xs btn-primary"
                    :to="{ name: 'repairs_edit', params: { id: props.row.id } }"
                >
                    <i class="fas fa-edit fa-fw"></i>
                </router-link>
            </template>
        </v-server-table>
    </card>
</template>

<script>
import { mapState } from "vuex";
import $http from "@/helpers/api";
import { IDLE_PERIODS, LAST_PERIOD_MIN, EMPTY_IDLE_FILTERS } from "./idlePeriods";

// Mirrors IdleRepairsQuery::ENDED_STATUS_IDS: ended repairs never show up as idle
const ENDED_STATUS_IDS = [6, 7];

// Outline color of a days group: hue from green (1 day) to red (the last period), grey for today
function dayColor(days, alpha = 1) {
  if (days < 1) {
    return `hsla(210, 11%, 71%, ${alpha})`;
  }

  const hue = Math.round(120 * (1 - (Math.min(days, LAST_PERIOD_MIN) - 1) / (LAST_PERIOD_MIN - 1)));

  return `hsla(${hue}, 70%, 45%, ${alpha})`;
}

// The server sends rows already ordered by days idle; a header row opens each day
// and the last row of each one is flagged, so the group can be outlined
function groupByDay(rows) {
  const grouped = [];

  rows.forEach((row, index) => {
    const days = row.days_since_opening;
    const next = rows[index + 1];

    if (!index || days !== rows[index - 1].days_since_opening) {
      grouped.push({ id: `group-${index}`, is_group: true, days });
    }

    grouped.push({ ...row, days, group_last: !next || next.days_since_opening !== days });
  });

  return grouped;
}

function rowClass(row) {
  const classes = ["idle-group"];

  if (row.is_group) {
    classes.push("idle-group-header");
  }
  if (row.group_last) {
    classes.push("idle-group-last");
  }

  return classes.join(" ");
}

function rowAttributes(row) {
  return { style: `--idle-color: ${dayColor(row.days)}; --idle-bg: ${dayColor(row.days, 0.15)}` };
}

// The exact day when it fits the range, otherwise none ("" means every day)
function validDays(days, { min, max }) {
  return days === "" || (days >= min && days <= max) ? days : "";
}

// Send only supported filter fields to the API.
function filterValues(filters) {
  return Object.keys(EMPTY_IDLE_FILTERS).reduce((values, key) => {
    values[key] = filters[key] == null ? "" : filters[key];
    return values;
  }, {});
}

export default {
  name: "RepairsIdleTable",
  props: {
    // One canonical state in Dashboard.vue; controls emit patches instead of mutating props.
    filters: {
      type: Object,
      default: () => ({ ...EMPTY_IDLE_FILTERS })
    }
  },
  computed: {
    ...mapState("asfs", {
      asfs: "all"
    }),
    statusList() {
      return this.$store.getters["repairs/getStatusList"] || [];
    },
    idleStatusList() {
      return this.statusList.filter(status => !ENDED_STATUS_IDS.includes(status.id));
    },
    asf_value: {
      get() {
        const id = this.filters.asf;
        return id ? (this.asfs || []).find(item => item.id === Number(id)) || { id, name: "", vat: "" } : null;
      },
      set(asf) {
        this.setFilter("asf", asf ? asf.id : "");
      }
    },
    searchQuery() {
      return this.tableReady ? this.$refs.vuetable.query || "" : "";
    },
    activeFilters() {
      const labels = {
        status_id: this.$t("field.status"),
        asf: this.$t("field.asf"),
        period: this.$t("dashboard.idlerepairs_period"),
        days: this.$t("dashboard.idlerepairs_days"),
        date_from: this.$t("dashboard.idlerepairs_date_from"),
        date_to: this.$t("dashboard.idlerepairs_date_to")
      };
      const active = Object.keys(labels).filter(key => this.filters[key] !== "" && this.filters[key] != null).map(key => {
        let value = this.filters[key];
        if (key === "status_id") {
          const status = this.statusList.find(status => status.id === Number(value));
          value = status ? status.name : value;
        } else if (key === "asf") {
          value = this.asf_value.name || `#${value}`;
        } else if (key === "period") {
          value = this.periodLabel(value);
        } else if (key === "date_from" || key === "date_to") {
          value = this.$moment(value).format("DD/MM/YYYY");
        }
        return { key, label: `${labels[key]}: ${value}` };
      });
      if (this.searchQuery) {
        active.push({ key: "query", label: `${this.$t("dashboard.idlerepairs_search")}: ${this.searchQuery}` });
      }
      return active;
    },
    // Bounds of the days filter: the selected period, or up to the most idle repair without one
    daysRange() {
      const [min, max] = this.filters.period ? this.filters.period.split(",").map(Number) : [0];

      return { min, max: max || Math.max(min, this.maxDays) };
    },
    // Keep a custom selected range visible even when it is not in IDLE_PERIODS.
    periodOptions() {
      return !this.filters.period || this.periods.includes(this.filters.period)
        ? this.periods
        : [...this.periods, this.filters.period];
    }
  },
  data() {
    // Id of the latest datatable request; vue-tables-2 applies every response it gets, even late ones
    let latestRequest = 0;

    return {
      periods: IDLE_PERIODS,
      maxDays: 0,
      exporting: false,
      tableReady: false,
      refreshQueued: false,
      clearSearchRequested: false,
      dateFormat: { year: "numeric", month: "numeric", day: "numeric" },
      columns: [
        "repair_id",
        "service_number",
        "name",
        "customer_name",
        "model_name",
        "serial_number",
        "warranty",
        "status_name",
        "opened_at",
        "days_since_opening",
        "id"
      ],
      options: {
        perPage: 10,
        perPageValues: [10, 25, 50, 100],
        headings: {
          repair_id: () => this.$t("field.repair_id"),
          service_number: () => this.$t("field.service_number"),
          name: () => this.$t("field.asf"),
          customer_name: () => this.$t("field.customer_name"),
          model_name: () => this.$t("field.model"),
          serial_number: () => this.$t("repair.serialnumber"),
          warranty: () => this.$t("field.warranty"),
          status_name: () => this.$t("field.status"),
          opened_at: () => this.$t("dashboard.idlerepairs_opened_at"),
          days_since_opening: () => this.$t("dashboard.idlerepairs_days_since_opening"),
          id: ""
        },
        sortable: [
          "repair_id",
          "service_number",
          "name",
          "customer_name",
          "model_name",
          "serial_number",
          "warranty",
          "status_name",
          "opened_at",
          "days_since_opening"
        ],
        texts: {
          count: this.$t("field.count"),
          filter: this.$t("field.filter"),
          filterPlaceholder: this.$t("field.filterplaceholder"),
          limit: this.$t("field.limit")
        },
        orderBy: {
          ascending: false,
          column: "days_since_opening"
        },
        columnsClasses: { id: "text-right", days_since_opening: "text-center" },
        // An outdated response resolves to undefined, which vue-tables-2 skips: no rows swapped, no `loaded`
        requestFunction(data) {
          const request = ++latestRequest;

          // The API instance: it has the API base URL and the session token
          return $http
            .get("/dtable/repairs_idle", { params: data })
            .then(response => (request === latestRequest ? response : undefined))
            .catch(e => {
              if (request === latestRequest) {
                this.dispatch("error", e);
              }
            });
        },
        responseAdapter(resp) {
          const { data, count } = this.getResponseData(resp);

          return { data: groupByDay(data), count };
        },
        rowClassCallback: rowClass,
        rowAttributesCallback: rowAttributes,
        skin: "table table-sm table-hover",
        pagination: {
          edge: true
        },
        params: filterValues(this.filters),
        sortIcon: {
          base: "fas fa-sort",
          up: "fas fa-sort-up",
          down: "fas fa-sort-down",
          is: "fas fa-sort"
        }
      }
    };
  },
  watch: {
    filters: {
      deep: true,
      handler(filters, previous) {
        if (filters.asf !== previous.asf) {
          this.loadSelectedAsf();
        }
        this.refresh();
      }
    }
  },
  created() {
    if (!this.statusList || !this.statusList.length) {
      this.$store.dispatch("repairs/getStatusList");
    }
    this.loadSelectedAsf();
  },
  mounted() {
    this.tableReady = true;
  },
  methods: {
    setFilter(key, value) {
      this.$emit("change", { [key]: value });
    },
    removeFilter(key) {
      if (key === "query") {
        this.clearSearchRequested = true;
        this.refresh();
      } else {
        this.setFilter(key, "");
      }
    },
    asfLabel({ name, vat }) {
      return `${name} — ${vat}`;
    },
    getAsfs() {
      this.$store.dispatch("asfs/getAllAsfs", "name");
    },
    periodLabel(period) {
      const [min, max] = period.split(",");

      if (!max) {
        return this.$t("dashboard.idlerepairs_period_label_over", { min });
      }

      return this.$t("dashboard.idlerepairs_period_label", { min, max });
    },
    groupLabel(days) {
      return days < 1
        ? this.$t("dashboard.idlerepairs_period_label_today")
        : this.$tc("dashboard.idlerepairs_day_label", days, { n: days });
    },
    // Applied on change (not on every key) so typing a number is not cut short; out of range snaps to the bound
    onDaysChange(event) {
      const value = event.target.value;

      if (value === "") {
        this.setFilter("days", "");
        return;
      }

      const { min, max } = this.daysRange;
      const days = Math.min(Math.max(Math.round(Number(value)), min), max);

      event.target.value = days;
      this.setFilter("days", days);
    },
    // Only the latest response gets here (see requestFunction). A day the new maximum rules out is
    // cleared, not moved, through the same canonical filter state.
    onLoaded(response) {
      const body = response && response.data ? response.data : response;

      this.maxDays = (body && body.max_days) || 0;
      const days = validDays(this.filters.days, this.daysRange);
      if (days !== this.filters.days) {
        this.setFilter("days", days);
      }
    },
    loadSelectedAsf() {
      if (this.asf_value && !this.asf_value.name) {
        this.getAsfs();
      }
    },
    clearFilters() {
      this.$emit("clear");
      this.clearSearchRequested = true;
      this.refresh();
    },
    // Every row under the current filters, search and order (not just the visible page)
    exportExcel() {
      const table = this.$refs.vuetable;
      const columns = this.columns.filter(column => column !== "id");
      const headings = this.options.headings;

      this.exporting = true;

      $http
        .get("/export/repairs_idle", {
          params: {
            ...filterValues(this.filters),
            query: table.query,
            orderBy: table.orderBy.column,
            ascending: table.orderBy.ascending ? 1 : 0
          }
        })
        .then(resp =>
          import("@/excel/Export2Excel").then(excel => {
            excel.export_json_to_excel({
              header: columns.map(column => headings[column]()),
              data: resp.data.map(row => columns.map(column => this.exportValue(row, column))),
              filename: `${this.$t("dashboard.idlerepairs_export_filename")}-${this.$moment().format("YYYY-MM-DD")}`,
              autoWidth: true,
              bookType: "xlsx"
            });
          })
        )
        .catch(() => {
          this.$notify({
            type: "danger",
            horizontalAlign: "center",
            message: this.$t("dashboard.idlerepairs_export_error")
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    exportValue(row, column) {
      if (column === "opened_at") {
        return this.$moment(row.opened_at).format("DD/MM/YYYY");
      }

      return row[column];
    },
    refresh() {
      // Coalesce prop updates and clearing search into one request with the latest criteria.
      if (this.refreshQueued) {
        return;
      }
      this.refreshQueued = true;
      this.$nextTick(() => {
        this.refreshQueued = false;
        const table = this.$refs.vuetable;
        Object.assign(this.options.params, filterValues(this.filters));
        const clearSearch = this.clearSearchRequested;
        this.clearSearchRequested = false;
        if (clearSearch && table.query) {
          table.setFilter("");
        } else {
          table.refresh();
        }
      });
    }
  }
};
</script>

<style>
/* Each days group is outlined in its color (set per row as --idle-color / --idle-bg):
   sides on every row, top on the header, bottom on the last row.
   Global because rows are rendered inside vue-tables-2, out of reach of scoped styles */
tr.idle-group td:first-child {
  border-left: 2px solid var(--idle-color);
}
tr.idle-group td:last-child {
  border-right: 2px solid var(--idle-color);
}
tr.idle-group-header td {
  border-top: 2px solid var(--idle-color);
}
tr.idle-group-last td {
  border-bottom: 2px solid var(--idle-color);
}

/* The header row has every column (vue-tables-2 gives no colspan): the title is taken out of
   the flow so it runs across the empty cells without widening the first column */
.table-hover tbody tr.idle-group-header,
.table-hover tbody tr.idle-group-header:hover {
  background-color: var(--idle-bg);
}
/* OpticSans has no face above 500: the theme's 600 adds a synthetic bold on top of the Heavy face */
.btn.idle-action {
  font-weight: 500;
}
.idle-active-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.btn.idle-filter-chip {
  margin: 0;
  white-space: normal;
  text-align: left;
}
.btn.idle-filter-chip:focus {
  outline: 2px solid #212529;
  outline-offset: 3px;
}
.idle-group-title {
  position: relative;
  height: 1.5em;
}
.idle-group-title span {
  position: absolute;
  top: 0;
  left: 0;
  font-weight: 500;
  white-space: nowrap;
}
</style>
